import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const isBackNavigation = (state: unknown) => (state as { direction?: string } | null)?.direction === 'back';

/**
 * Convidar, cadastro de Ação e cadastro de Realização Destaque voltam à Home com
 * `state: { direction: 'back' }` para o saldo ser relido. O `state` é consumido uma vez: a
 * entrada do histórico é trocada pela mesma rota sem ele, então nem um re-render nem o F5
 * (o `history.state` sobrevive ao recarregar) disparam outro refresh (DCE-02, DCE-03).
 *
 * Depende só de `location.key`: uma nova referência de `refreshUser` ou de `user` não é uma
 * nova navegação (DCE-04).
 *
 * Devolve `true` enquanto esse refresh não termina: quem relê depois dele (`userRevision`) espera,
 * em vez de ler na montagem e de novo quando ele conclui (DCE-57). Uma falha também libera.
 */
export function useBackNavigationRefresh(refreshUser: () => Promise<void> | void): boolean {
  const location = useLocation();
  const navigate = useNavigate();
  const [waitingRefresh, setWaitingRefresh] = useState(() => isBackNavigation(location.state));

  // O replace abaixo troca o `location.key`; a espera não pode acabar com ele, só com a desmontagem.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isBackNavigation(location.state)) return;

    setWaitingRefresh(true);
    Promise.resolve(refreshUser())
      .catch(() => undefined)
      .then(() => {
        if (mountedRef.current) setWaitingRefresh(false);
      });
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return waitingRefresh;
}
